/**
 * @module useReportsRoutes
 * @description Hook for managing reports navigation routes and links
 */
import { useSelector } from 'react-redux';
import { translate } from '../../../utils/locale';
import {
	getImpersonationMode,
	getSubscriptionStatus,
	hasGroupAccess,
} from 'features/app/redux/context';
import { knowledgeBaseRoutes } from '../../common/knowledgeBaseRoutes';
import { getFeature } from 'features/app/redux/features';

/**
 * Hook that returns the navigation routes for reports and downloads
 * @returns {Object} Navigation route configuration object
 * @property {SecondaryNavigationLinks[]} reportsRoutes - Array of reports navigation links
 * @property {SecondaryNavigationLinks} reportsHomeRoutes - Reports home navigation configuration
 */
const useSummitReportsRoutes = (): {
	reportsSummitRoutes: SecondaryNavigationLinks[];
	reportsSummitHomeRoutes: SecondaryNavigationLinks;
} => {
	const subscriptionStatus = useSelector(getSubscriptionStatus);
	const canViewNewReports = useSelector(getFeature('new_group_reports'));
	const showHistoricalGroupReports = useSelector(getFeature('show_historical_group_reports'));
	const hasGroup = useSelector(hasGroupAccess);
	const impersonation = useSelector(getImpersonationMode);
	const isDisabled = impersonation !== 'Full' ? !subscriptionStatus.hasContract : !hasGroup;

	/**
	 * Array of reports navigation route configurations
	 * @type {SecondaryNavigationLinks[]}
	 */
	const reportsSummitRoutes: SecondaryNavigationLinks[] = [
		{
			isVisible: showHistoricalGroupReports,
			withInternalUrl: '/groups/reports/group-reports',
			icon: 'file',
			displayName: translate('home.groups.reportsDownloads.links.GROUP_REPORTS') as string,
			disabled: isDisabled,
		},
		{
			isVisible: canViewNewReports,
			withInternalUrl: '/groups/reports/group-reports-new',
			icon: 'file',
			displayName: translate('home.groups.reportsDownloads.links.NEW_GROUP_REPORTS') as string,
			disabled: isDisabled,
		},
		{
			isVisible: true,
			withInternalUrl: '/groups/reports/school-and-college-reports',
			icon: 'file',
			displayName: translate('home.groups.reportsDownloads.links.SCHOOL_COLLEGE') as string,
			disabled: isDisabled,
		},
		{
			isVisible: true,
			withInternalUrl: '/groups/reports/additional-downloads',
			icon: 'file',
			displayName: translate(
				'home.singleSchool.reportsDownloads.links.ADDITIONAL_DOWNLOADS'
			) as string,
			disabled: isDisabled,
		},
	];

	/**
	 * Home navigation route configuration with flyout menu
	 * @type {SecondaryNavigationLinks}
	 */
	const reportsSummitHomeRoutes: SecondaryNavigationLinks = {
		displayName: translate('home.groups.reportsDownloads.HEADING') as string,
		isVisible: subscriptionStatus.hasContract,
		disabled: false,
		icon: 'file-chart-column',
		flyoutContent: [
			{
				heading: 'Report Management',
				flyoutLinks: reportsSummitRoutes,
			},
			knowledgeBaseRoutes,
		],
	};

	return {
		reportsSummitRoutes,
		reportsSummitHomeRoutes,
	};
};

export default useSummitReportsRoutes;
